<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <create-bill
      v-if="showCreateBilling"
      :academic-years="this.filterOptions.academicYears"
      :default-provider-id="this.userCompanies[0]"
      :get-months="getMonthsByAcademicYearId"
      :get-providers="getProvidersByAcademicYear"
      @on-close="() => (showCreateBilling = false)"
      @on-submit="submitBill"
    />
    <filter-manager
      v-if="!showCreateBilling"
      ref="filterRef"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @academicYear="onChangeAcademicYear"
      @providerType="onChangeProviderType"
      @search="onSearchHandler"
    />
    <custom-separator class="mt-1 mb-4" text="Resultado de Búsqueda" />
    <div
      v-if="results.length > 0 && processPaymentVisible"
      class="col d-flex justify-content-end align-items-center mb-2"
    >
      <check-box
        v-model="allSelected"
        class="w-auto color-green underline font-15"
        text="Seleccionar Todos"
        @input="onSelectAll"
      />
    </div>
    <custom-result-not-found
      v-if="results.length == 0"
      :text="'No hay información disponible.'"
    />
    <template v-else>
      <bill-result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        ref="billResultRow"
        class="mb-3"
        :data="row"
        :visible-process-check="processPaymentVisible"
        @on-reload="updateResults()"
        @on-view-bill="onViewBillHandler(row)"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import Pagination from 'pagination';
import CustomSeparator from 'custom-separator';
import CheckBox from 'CheckBox';
import CustomResultNotFound from 'custom-result-not-found';
import BillResultRow from './components/BillResultRow';
import CreateBill from './components/CreateBill';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';
import storeMixin from '@quisit/mipe-utils/mixins/storeGenerator';
import {
  getInitFilters as _getInitFilters,
  getMonthsInAcademicYear as _getMonthsInAcademicYear,
  search as _search,
  createBill as _createBill,
  processBills as _processBills,
} from '@/services/provider/billing-history.api.js';
import {
  getProviderOptions as _getProviderOptions,
  getProvidersByAcademicYear as _getProvidersByAcademicYear,
} from '@/services/provider/provider.api.js';
import storeModuleNames from '@/utils/constants/storeModuleNames.js';
import roles from '@/utils/constants/roles.js';
import billingStatus from '@/utils/constants/billingStatus.js';
import { mapGetters } from 'vuex';

const DEFAULT_PAGE_SIZE = 20;
const BILLING_HISTORY_MANAGEMENT_ROUTE_NAME = 'billingHistoryManagement';

export default {
  name: 'BillingHistoryManagementMain',
  mixins: [alertMixin, storeMixin],
  components: {
    PageHeader,
    Pagination,
    CheckBox,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    BillResultRow,
    CreateBill,
  },
  data: () => ({
    moduleName: storeModuleNames.ProviderBillingHistoryModuleName,
    showCreateBilling: false,
    filterModel: {
      academicYear: null,
      status: null,
      period: null,
      provider: null,
      billNumber: null,
    },
    filterOptions: {
      academicYears: [],
      statuses: [],
      periods: [],
      providerTypes: [],
      providers: [],
      interveners: [],
    },
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
    processPaymentVisible: false,
  }),
  computed: {
    allSelected() {
      return this.results.every((row) => row.selected);
    },
    ...mapGetters('$_user', ['fullProfile', 'companies']),
    headerConfig() {
      return {
        title: 'Historial de Facturas',
        icon: 'fas fa-user-tie',
        routes: ['Inicio', 'Proveedor', 'Historial de Facturas'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Procesar Pago',
          icon: 'fas fa-solid fa-file-invoice-dollar pr-2 font-20',
          variant: 'outline-success ml-0',
          visible: this.processPaymentVisible,
          click: () => this.showConfirmationProcessPayment(),
        },
        {
          text: 'Crear Factura',
          icon: 'fas fa-file-plus pr-2 font-20',
          variant: 'success ml-0',
          visible: this.canAddInvoice,
          click: () => this.onAddBillHandler(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'academicYear',
          component: 'CustomDropDown',
          options: this.filterOptions.academicYears,
          placeholder: 'Año Académico',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: false,
        },
        {
          name: 'period',
          component: 'CustomDropDown',
          options: this.filterOptions.periods,
          placeholder: 'Periodo',
          fieldName: 'monthDesc',
          fieldKey: 'monthNumber',
          class: 'col-12 col-md-4',
        },
        {
          name: 'status',
          component: 'CustomDropDown',
          options: this.filterOptions.statuses,
          placeholder: 'Estatus',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
        },
        {
          name: 'providerType',
          component: 'CustomDropDown',
          options: this.filterOptions.providerTypes,
          label: 'Tipo de proveedor:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-2',
          isAdvanced: true,
        },
        {
          name: 'provider',
          component: 'CustomDropDown',
          options: this.filterOptions.providers,
          label: 'Proveedor:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
        {
          name: 'intervenedBy',
          component: 'CustomDropDown',
          options: this.filterOptions.interveners,
          label: 'Intervenido Por:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
        {
          name: 'billNumber',
          component: 'CustomInput',
          label: 'Número de Factura:',
          placeholder: 'Escriba número de factura',
          value: this.filterModel.billNumber,
          class: 'col-12 col-md-3',
          isAdvanced: true,
        },
      ];
    },
    canAddInvoice() {
      let permission = this.userHasPermissions(
        'rempro.providers.billinghistory.createbill'
      );
      return permission && !this.showCreateBilling;
    },
    isProviderRp() {
      return this.fullProfile?.roleid?.toUpperCase() === roles.Provider_RP;
    },
    hasPersistInfo() {
      return !!this.moduleData?.BillingHistoryManagementMain;
    },
    modulePersistedData() {
      return this.moduleData?.BillingHistoryManagementMain;
    },
    providerCompanyIds() {
      return this.companies.map((x) => x.id);
    },
  },
  async mounted() {
    this.setStatuses();
    await this.getInitFilters();
    await this.onSearchHandler();
  },
  destroyed() {
    this.persistData();
  },
  methods: {
    async updateResults() {
      await this.onSearchHandler();
    },
    setStatuses() {
      if (this.hasPersistInfo) return;

      let availableStatuses = [];
      if (this.userRoleId !== roles.Pre_Interventor_RP) {
        availableStatuses.push({
          id: billingStatus.Preliminar,
          name: 'Preliminar',
        });
      }
      availableStatuses.push({
        id: billingStatus.PendienteIntervencion,
        name: 'Pendiente Intervención',
      });
      availableStatuses.push({
        id: billingStatus.EnProcesoIntervencion,
        name: 'En Proceso de Intervención',
      });
      availableStatuses.push({
        id: billingStatus.PendienteRevision,
        name: 'Pendiente Revisión',
      });
      availableStatuses.push({
        id: billingStatus.PendienteIntervencionObjetada,
        name: 'Pendiente Intervención Objetada',
      });
      availableStatuses.push({
        id: billingStatus.EnProcesoIntervencionObjetada,
        name: 'En Proceso de Intervención Objetada',
      });
      availableStatuses.push({
        id: billingStatus.PendienteAprobacion,
        name: 'Pendiente Aprobación',
      });
      availableStatuses.push({ id: billingStatus.Aprobado, name: 'Aprobado' });
      availableStatuses.push({
        id: billingStatus.EnviadoFinanzas,
        name: 'Enviado a Finanzas',
      });
      availableStatuses.push({
        id: billingStatus.EnviadoFinanzasParcial,
        name: 'Enviado a Finanzas Parcial',
      });
      availableStatuses.push({
        id: billingStatus.DevueltoTPFA,
        name: 'Devuelto por TPFA',
      });
      availableStatuses.push({
        id: billingStatus.EnProcesoPago,
        name: 'En Proceso de Pago',
      });
      availableStatuses.push({ id: billingStatus.Pagado, name: 'Pagado' });
      availableStatuses.push({
        id: billingStatus.Cancelada,
        name: 'Cancelada',
      });

      this.filterOptions.statuses = availableStatuses;
    },
    async getInitFilters() {
      if (this.hasPersistInfo) return;

      const { filters, collections } = (await _getInitFilters()).data;

      this.filterModel = {
        ...this.filterModel,
        ...filters,
      };
      this.filterOptions = {
        ...this.filterOptions,
        ...collections,
      };

      this.onChangeAcademicYear(this.filterModel.academicYear);
    },
    async onChangeAcademicYear(value) {
      if (value?.id) {
        const { data } = await _getMonthsInAcademicYear(value.id);
        this.filterOptions.periods = data;
        this.filterModel.period = null;
        this.filterModel.provider = null;
        this.setProviderOptions();
      }
    },
    async onChangeProviderType() {
      this.setProviderOptions();
    },
    async onAddBillHandler() {
      this.showCreateBilling = !this.showCreateBilling;
    },
    closeAllResultPanel() {
      if (this.results.length > 0) {
        this.results.forEach((_, idx) => {
          this.$refs.billResultRow[idx].showPanel = false;
        });
      }
    },
    async onSearchHandler() {
      this.closeAllResultPanel();
      if (!this.companies?.length && this.isProviderRp) return;

      const pageIndex = this.modulePersistedData?.pagination?.currentPage
        ? this.modulePersistedData.pagination.currentPage
        : 1;

      const payload = this.fillPayload(pageIndex);
      this.searchBills(payload);
      this.isProcessPaymentVisible(); //Set visible process payment button and checks
    },
    async searchBills(payload) {
      const res = (await _search(payload)).data;
      this.results = res.data.map((x) => ({
        ...x,
        selected: false, //Add Properties for selected and dirty
      }));
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      this.closeAllResultPanel();
      const payload = this.fillPayload(page);
      await this.searchBills(payload);
    },
    fillPayload(page = 1) {
      let companies = [];

      if (!this.filterModel.provider?.id) companies = this.providerCompanyIds;

      const payload = {
        academicYearId: this.filterModel.academicYear?.id,
        billingStatusId: this.filterModel.status?.id,
        monthNumber: this.filterModel.period?.monthNumber,
        companyTypeId: this.filterModel.providerType?.id,
        companyId: this.filterModel.provider?.id,
        intervenedBy: this.filterModel.intervenedBy?.id,
        billNumber: this.filterModel.billNumber,
        userCompanies: companies,
        pageIndex: page,
        pageSize: this.pagination.pageSize,
      };
      return payload;
    },
    onViewBillHandler({ billId }) {
      this.$router.push({
        name: BILLING_HISTORY_MANAGEMENT_ROUTE_NAME,
        params: {
          billId,
          info: this.filterModel,
        },
      });
    },
    getMonthsByAcademicYearId() {
      return _getMonthsInAcademicYear;
    },
    getProvidersByAcademicYear() {
      return _getProvidersByAcademicYear;
    },
    async submitBill(value) {
      try {
        const { data } = await _createBill({
          academicYearId: value.academicYear.id,
          month: value.month.monthNumber,
          year: value.month.year,
          companyId: value.provider.id,
        });
        this.$router.push({
          name: 'billingHistoryManagement',
          params: {
            billId: data,
          },
        });
      } catch (exception) {
        var message = exception.response.data.message;
        this.ShowToast('Alerta', message, 'error');
      }
      this.showCreateBilling = false;
    },
    onSelectAll(value) {
      if (!value && !this.results.every((row) => row.selected)) {
        return;
      }

      this.results.forEach((x) => {
        x.selected = value;
      });
    },
    isProcessPaymentVisible() {
      var statusSelected = this.filterModel.status;

      const allowStatus = [
        billingStatus.Aprobado,
        billingStatus.EnviadoFinanzasParcial,
        billingStatus.EnviadoFinanzas,
        billingStatus.DevueltoTPFA,
      ];

      if (statusSelected !== null && statusSelected !== undefined) {
        this.processPaymentVisible =
          allowStatus.includes(statusSelected.id) &&
          this.userHasPermissions(
            'rempro.providers.billinghistory.processpayment'
          );
      } else {
        this.processPaymentVisible = false;
      }
    },
    async showConfirmationProcessPayment() {
      const isBillSelected = this.results.some((x) => x.selected);
      if (!isBillSelected) {
        this.ShowToast(
          'Alerta',
          'Favor seleccione al menos una factura para procesar el pago.',
          'error'
        );

        return;
      }

      const answer = await this.ShowCancelModal(
        'Confirmación',
        `¿Está seguro(a) que desea procesar el pago de la(s) factura(s) seleccionada(s)?`,
        'Sí',
        'No'
      );

      if (!answer) {
        return;
      }

      this.onSumbitPayment();
    },
    onSumbitPayment() {
      var billIds = this.results.filter((x) => x.selected).map((x) => x.billId);

      _processBills(billIds)
        .then(() => {
          this.ShowToast(
            'Alerta',
            'Se ha enviado la factura a finanzas exitosamente.',
            'success'
          );
          this.onSearchHandler();
        })
        .catch((err) => {
          this.ShowToast('Alerta', err.response.data.message, 'error');
        });
    },
    async setProviderOptions() {
      const { data } = await _getProviderOptions({
        status: 0,
        academicYearId: this.filterModel.academicYear?.id,
        companyIds: this.providerCompanyIds,
        companyType: this.filterModel.providerType?.id,
      });

      this.filterOptions.providers = data;
      this.filterModel.provider = null;

      if (this.isProviderRp && this.companies?.length === 1) {
        const { id, name } = this.companies[0];
        const currentCompanyIsSelectable = this.filterOptions.providers.find(
          (provider) => provider.id == id
        );

        let selectedProvider = {
          id,
          name,
          disabled: true,
          sortOrder: 0,
          isDirty: false,
          selected: true,
          keyId: '00000000-0000-0000-0000-000000000000',
        };

        this.filterOptions.providers = currentCompanyIsSelectable
          ? [selectedProvider]
          : [];
        this.filterModel.provider = currentCompanyIsSelectable
          ? selectedProvider
          : null;
      }
    },
  },
  watch: {
    providerCompanyIds() {
      this.setProviderOptions();
    },
  },
};
</script>
<style scoped lang="scss">
.underline {
  text-decoration: underline;
}
</style>
